import React, { useMemo, useState } from "react";
import { FeatherChevronDown, FeatherChevronRight } from "@subframe/core";
import {
  TallySyncErrorItem,
  TallySyncItem,
  TallySyncReport,
  TallySyncSkippedItem,
} from "../types/tallySyncReport";

type DetailBucket = "created" | "updated" | "deleted" | "skipped" | "errors";

const BUCKET_LABELS: Record<DetailBucket, string> = {
  created: "Created",
  updated: "Updated",
  deleted: "Deleted",
  skipped: "Skipped",
  errors: "Errors",
};

function formatItem(item: TallySyncItem | TallySyncSkippedItem | TallySyncErrorItem): string {
  if ("reason" in item) {
    const name = "name" in item && item.name ? item.name : "(unnamed)";
    return `${name} — ${item.reason}`;
  }
  return item.name || "(unnamed)";
}

interface SyncReportPanelProps {
  report: TallySyncReport;
}

export const SyncReportPanel: React.FC<SyncReportPanelProps> = ({ report }) => {
  const [expanded, setExpanded] = useState<DetailBucket | null>(null);

  const buckets = useMemo(
    () =>
      (Object.keys(BUCKET_LABELS) as DetailBucket[]).map((key) => ({
        key,
        label: BUCKET_LABELS[key],
        count: report.summary[key],
        items: report.details[key],
      })),
    [report],
  );

  const isSuccess = report.success && report.meta?.implemented !== false;
  const borderClass = isSuccess
    ? "border-green-200 bg-green-50"
    : report.meta?.implemented === false
      ? "border-amber-200 bg-amber-50"
      : "border-red-200 bg-red-50";
  const titleClass = isSuccess
    ? "text-green-800"
    : report.meta?.implemented === false
      ? "text-amber-900"
      : "text-red-700";
  const bodyClass = isSuccess
    ? "text-green-700"
    : report.meta?.implemented === false
      ? "text-amber-800"
      : "text-red-700";

  return (
    <div
      className={`flex w-full flex-col items-start gap-3 rounded-lg border border-solid p-4 ${borderClass}`}
    >
      <div className="flex w-full flex-col items-start gap-1">
        <span className={`text-body font-body font-semibold ${titleClass}`}>
          {isSuccess
            ? "Sync completed"
            : report.meta?.implemented === false
              ? "Not available yet"
              : "Sync finished with issues"}
        </span>
        {report.message ? (
          <span className={`text-sm ${bodyClass}`}>{report.message}</span>
        ) : null}
        {report.meta?.dry_run ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Dry-run — no changes were written
          </span>
        ) : null}
        {report.meta?.staged != null && report.meta.staged > 0 ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Staged from Tally: {report.meta.staged}
          </span>
        ) : null}
        {report.meta?.requeued != null && report.meta.requeued > 0 ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Re-queued from Tally: {report.meta.requeued}
          </span>
        ) : null}
        {report.meta?.content_unchanged != null && report.meta.content_unchanged > 0 ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Content unchanged (no write): {report.meta.content_unchanged}
          </span>
        ) : null}
        {report.meta?.watermark_from ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Export filter from {report.meta.watermark_from}
            {report.meta.watermark_source ? ` (${report.meta.watermark_source})` : ""}
          </span>
        ) : report.meta?.watermark_source === "none" ? (
          <span className={`text-caption font-caption ${bodyClass}`}>
            Full voucher export (no date filter)
          </span>
        ) : null}
      </div>

      <div className={`flex w-full flex-wrap gap-4 text-sm ${bodyClass}`}>
        {buckets.map(({ key, label, count }) => (
          <div key={key} className="flex items-center gap-1">
            <span className="font-semibold">{label}:</span>
            <span className={key === "errors" && count > 0 ? "text-red-600" : undefined}>
              {count}
            </span>
          </div>
        ))}
      </div>

      <div className="flex w-full flex-col gap-2">
        {buckets.map(({ key, label, count, items }) => {
          if (count === 0) return null;
          const isOpen = expanded === key;
          return (
            <div key={key} className="flex w-full flex-col gap-1">
              <button
                type="button"
                className={`flex items-center gap-1 text-left text-sm font-semibold ${bodyClass}`}
                onClick={() => setExpanded(isOpen ? null : key)}
              >
                {isOpen ? (
                  <FeatherChevronDown className="h-4 w-4" />
                ) : (
                  <FeatherChevronRight className="h-4 w-4" />
                )}
                {label} ({count})
              </button>
              {isOpen ? (
                <ul className={`list-disc list-inside text-xs pl-5 ${bodyClass}`}>
                  {items.map((item, index) => (
                    <li key={`${key}-${index}`}>{formatItem(item)}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
